import { Component, createRef, RefObject } from 'react'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import currency from 'currency.js'

import { selectEmployeeByUuid } from '../../payroll.selectors'
import FixedCell from './FixedCell'
import { Button, Text, Form, Dialog } from '@/ui'
import { AddButton, EditButton } from './PayrollInputButtons'
import { PayrollComps } from '../../helpers'
import { useReselector } from '@/utils/sharedHooks'
import { Gusto_EmployeeFixedCompensation } from '../../generated_gusto_types'
// TODO: Migrate to V2
import { getFieldName, makeNumberSchema } from '@/base'
import { TypographyStyles } from '@/styles'

const validationSchema = yup.object({
  cashTips: makeNumberSchema({ field: 'cash tips', allowedDecimals: 2 }),
  paycheckTips: makeNumberSchema({
    field: 'paycheck tips',
    allowedDecimals: 2,
  }),
  minimumWageAdjustment: makeNumberSchema({
    field: 'minimum wage adjustment',
    allowedDecimals: 2,
  }),
  commission: makeNumberSchema({ field: 'commission', allowedDecimals: 2 }),
})

export const AdditionalEarningsModal = ({
  onSave,
  close,
  open,
  employeeUuid,
  cashTips,
  paycheckTips,
  minimumWageAdjustment,
  commission,
}: {
  onSave: (_: {
    cashTips: string
    paycheckTips: string
    minimumWageAdjustment: string
    commission: string
  }) => void
  close: () => void
  open: boolean
  employeeUuid: string
  cashTips: string
  paycheckTips: string
  minimumWageAdjustment: string
  commission: string
}) => {
  const employee = useReselector(selectEmployeeByUuid, employeeUuid)

  const formik = useFormik({
    initialValues: {
      cashTips,
      paycheckTips,
      minimumWageAdjustment,
      commission,
    },
    validationSchema,
    onSubmit: (values) => onSave(values),
  })

  return (
    <Dialog open={open} onOpenChange={close}>
      <Dialog.Content className="p-8">
        <Dialog.Title>Add Other Earnings</Dialog.Title>
        <FormikProvider value={formik}>
          <div className="flex flex-col gap-4">
            <Text variant={TypographyStyles.BODY_SM} className="mt-2">
              {employee?.first_name} {employee?.last_name} will be paid these
              earnings, in addition to their regular wages.
            </Text>

            <Form.Input
              name={getFieldName<typeof formik.values>('commission')}
              componentType="currency"
              label="Commission"
              description="The amount of commission pay the employee received for this pay period."
            />

            <Form.Input
              name={getFieldName<typeof formik.values>('paycheckTips')}
              componentType="currency"
              label="Paycheck Tips"
              description="Paycheck tips (service charges) to be paid to the employee this pay period. This amount will be added to the employee's gross pay."
            />

            <Form.Input
              name={getFieldName<typeof formik.values>('minimumWageAdjustment')}
              componentType="currency"
              label="Minimum Wage Adjustment"
              description="This amount will be added to the employee's gross pay."
            />

            <Form.Input
              name={getFieldName<typeof formik.values>('cashTips')}
              componentType="currency"
              label="Cash Tips"
              description="This amount will be added to the employee's gross pay."
            />

            <Dialog.Footer>
              <Button onClick={formik.submitForm} className="w-full">
                Save
              </Button>
              <Button onClick={close} variant="secondary" className="w-full">
                Cancel
              </Button>
            </Dialog.Footer>
          </div>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}

interface Props {
  fixedComps: Gusto_EmployeeFixedCompensation[]
  employeeUuid: string
  isOffCycle: boolean
}

interface State {
  modalShown: boolean
  cashTips: string
  paycheckTips: string
  minimumWageAdjustment: string
  commission: string
  jobUuid?: string
}

class AdditionalEarnings extends Component<Props, State> {
  bonusRef: RefObject<FixedCell>
  correctionRef: RefObject<FixedCell>

  constructor(props: Props) {
    super(props)

    this.state = {
      modalShown: false,
      cashTips:
        props.fixedComps.find((val) => val.name === PayrollComps.cashTips)
          ?.amount || '0.00',
      paycheckTips:
        props.fixedComps.find((val) => val.name === PayrollComps.paycheckTips)
          ?.amount || '0.00',
      minimumWageAdjustment:
        props.fixedComps.find(
          (val) => val.name === PayrollComps.minimumWageAdjustment
        )?.amount || '0.00',
      commission:
        props.fixedComps.find((val) => val.name === PayrollComps.commission)
          ?.amount || '0.00',
      jobUuid: props.fixedComps[0]?.job_uuid,
    }
    this.bonusRef = createRef<FixedCell>()
    this.correctionRef = createRef<FixedCell>()
  }

  calculate() {
    const {
      cashTips,
      paycheckTips,
      minimumWageAdjustment,
      commission,
      jobUuid: job_uuid,
    } = this.state
    return [
      {
        job_uuid,
        name: PayrollComps.bonus,
        amount: this.bonusRef.current?.calculate().amount || '0',
      },
      {
        job_uuid,
        name: PayrollComps.cashTips,
        amount: cashTips || '0',
      },
      {
        job_uuid,
        name: PayrollComps.commission,
        amount: commission || '0',
      },
      {
        job_uuid,
        name: PayrollComps.correctionPayment,
        amount: this.correctionRef.current?.calculate().amount || '0',
      },
      {
        job_uuid,
        name: PayrollComps.minimumWageAdjustment,
        amount: minimumWageAdjustment || '0',
      },
      {
        job_uuid,
        name: PayrollComps.paycheckTips,
        amount: paycheckTips || '0',
      },
    ]
  }

  saveModal = (payload: {
    cashTips: string
    paycheckTips: string
    minimumWageAdjustment: string
    commission: string
  }) => {
    const { employeeUuid } = this.props

    this.setState({ ...payload, modalShown: false }, () =>
      document.dispatchEvent(new CustomEvent(`inputChange${employeeUuid}`))
    )
  }

  render() {
    const {
      modalShown,
      commission,
      minimumWageAdjustment,
      cashTips,
      paycheckTips,
    } = this.state
    const { employeeUuid, fixedComps, isOffCycle } = this.props

    const otherTotal = currency(Number(commission))
      .add(Number(minimumWageAdjustment))
      .add(Number(cashTips))
      .add(Number(paycheckTips))

    const bonusComp = fixedComps.find(
      (val) => val.name === PayrollComps.bonus
    ) || {
      name: PayrollComps.bonus,
      amount: '0.00',
      job_uuid: this.state.jobUuid,
    }

    const correctionComp = fixedComps.find(
      (val) => val.name === PayrollComps.correctionPayment
    ) || {
      name: PayrollComps.correctionPayment,
      amount: '0.00',
      job_uuid: this.state.jobUuid,
    }

    return (
      <>
        <FixedCell
          ref={this.bonusRef}
          employeeUuid={employeeUuid}
          fixedComp={bonusComp}
        />
        {/*correction comp should only display for off cycle payrolls.  This is meant to match Gusto's implementation*/}
        {isOffCycle && (
          <FixedCell
            ref={this.correctionRef}
            employeeUuid={employeeUuid}
            fixedComp={correctionComp}
            name="Gross Earnings"
          />
        )}
        {otherTotal ? (
          <EditButton
            onClick={() => this.setState({ modalShown: true })}
            text={`$${otherTotal} Additional Pay`}
            style={{ marginTop: 5 }}
          />
        ) : (
          <AddButton
            onClick={() => this.setState({ modalShown: true })}
            text="Other Earnings"
          />
        )}
        {modalShown && (
          <AdditionalEarningsModal
            open={modalShown}
            employeeUuid={employeeUuid}
            close={() => this.setState({ modalShown: false })}
            onSave={this.saveModal}
            cashTips={cashTips}
            commission={commission}
            paycheckTips={paycheckTips}
            minimumWageAdjustment={minimumWageAdjustment}
          />
        )}
      </>
    )
  }
}

export default AdditionalEarnings
